import type { DateOrRange } from '@/infrastructure/departure-date/store'
import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilterLegacy/types'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { DatesCalendar, MonthCalendar } from '@/scopes/search/components/DepartureDateFilterLegacy/Calendars'
import { Chips } from '@/scopes/search/components/DepartureDateFilterLegacy/Chips'
import { useDepartureDateFilter } from '@/scopes/search/components/DepartureDateFilterLegacy/hooks'
import Tabs from '@/scopes/search/components/DepartureDateFilterLegacy/Tabs'
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBarLegacy/DialogPrimitive'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBarLegacy/store'
import {
  Button,
  ButtonLoaderIcon,
  Icon,
  SheetContentWrapper,
  SheetFooter,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { Item } from '../QuickSearchBarLegacy'

const PopoverContent = styled.div`
  padding: ${tk('spacing_md')} ${tk('spacing_md')} 0;
`
const FilterWrapper = styled.div`
  min-width: 850px;
  display: flex;
  justify-content: center;
  margin-top: 0 !important;
  padding-left: ${tk('spacing_md')};
  padding-right: ${tk('spacing_md')};
`
const PopoverHeader = styled(Tabs)`
  margin-bottom: ${tk('spacing_lg')};
`
const SheetHeader = styled(Tabs)`
  margin: ${tk('spacing_lg')} auto;
`

const SheetFooterStyled = styled(SheetFooter)`
  padding: unset;
  ${SheetFooter.modFixed()}
`

const DialogHeader = styled(DialogPrimitive.Header)`
  padding-bottom: ${tk('spacing_md')};
`
const Footer = styled.div`
  background-color: ${tk('color_semantic_bg')};
  bottom: 0;
  display: flex;
  padding-bottom: ${tk('spacing_md')};
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  z-index: 1;

  overflow-x: scroll;

  ${Button} {
    display: none;

    @media (min-width: 700px) {
      display: inline-flex;
    }
  }

  @media ${tk('mq_desktop_sm')} {
    overflow-x: initial;
    padding-top: ${tk('spacing_md')};
  }
`
const FooterButtonWrapper = styled(SpaceBetween)`
  margin-left: auto;

  button:enabled {
    span {
      margin-left: unset;
    }
  }
`
const DialogFooter = styled(DialogPrimitive.Footer)`
  @media (min-width: 700px) {
    display: none;
  }
`

const SheetView = styled(DepartureDateFilterByView)`
  padding: ${tk('spacing_md')};
  @media ${tk('mq_desktop_sm')} {
    margin-bottom: 0;
    margin-top: 0;
    width: auto;
  }
`

const SheetButtonWrapper = styled(SpaceBetween)`
  ${Button} {
    width: 100%;
  }
`

const SheetContentFooter = styled.div<{ $view: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${({ $view }) =>
    $view === 'calendar'
    && css`
      padding-top: ${tk('spacing_md')};
    `}
  ${SheetButtonWrapper} {
    width: 100%;
    padding: ${tk('spacing_md')};
    ${({ $view }) =>
      $view === 'calendar'
      && css`
        margin-top: ${tk('spacing_md')};
        box-shadow: 0px -2px 6px 0px rgba(0, 0, 0, 0.08);
      `}
  }

  @media (min-width: 700px) {
    flex-direction: row;
    justify-content: space-between;
    padding: ${tk('spacing_md')};

    ${SheetButtonWrapper} {
      margin-top: 0;
      padding: unset;
      box-shadow: none;
      ${({ $view }) =>
        $view === 'calendar'
        && css`
          width: auto;
        `}
    }
  }

  @media (${tk('mq_desktop_sm')}) {
    flex-direction: column;
    justify-content: center;
    ${({ $view }) =>
      $view === 'calendar'
      && css`
        padding: ${tk('spacing_md')} 0;
      `}
    ${SheetButtonWrapper} {
      width: 100%;
      ${({ $view }) =>
        $view === 'calendar'
        && css`
          margin-top: ${tk('spacing_md')};
          box-shadow: 0px -2px 6px 0px rgba(0, 0, 0, 0.08);
          padding: ${tk('spacing_md')};
        `}
    }
  }
`

function DepartureDateFilterByView({
  options,
  className,
}: {
  options: DepartureDateFilterOptions
  className?: string
}) {
  const { view } = useDepartureDateFilter({ options })
  if (view === 'calendar')
    return <DatesCalendar className={className} options={options} />
  return <MonthCalendar className={className} options={options} />
}

export function DepartureDateFilterComponentLegacy({
  disabled,
  onConfirm,
  onReset,
  options,
}: {
  onConfirm: ({ departureDate }: { departureDate: DateOrRange }) => void
  onReset: () => void
  options: DepartureDateFilterOptions
  disabled?: boolean
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()
  const { view, departureDate, reset } = useDepartureDateStore()
  const { formattedText } = useDepartureDateFilter({ options })

  const handleConfirm = () => {
    setActiveItem(null)

    if (disabled)
      return

    if (departureDate) {
      onConfirm({ departureDate })
    }
  }

  const handleReset = () => {
    onReset()
    reset()
    setActiveItem(null)
  }

  return (
    <Item.Root
      id="departure-date-filter"
      icon="IconCalendar"
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_title',
      })}
      onClose={handleConfirm}
    >
      <Item.AsPopover
        selectionFormattedText={formattedText?.long}
        alignment="start"
        disabled={disabled}
        avoidCollisions
        data-qa="departure-date-filter"
      >
        <PopoverContent>
          <PopoverHeader options={options} />
          <FilterWrapper>
            <DepartureDateFilterByView options={options} />
          </FilterWrapper>
          <Footer>
            {view === 'calendar' && <Chips options={options} />}

            <FooterButtonWrapper direction="x" spacing={tk('spacing_xs')}>
              <Button onClick={handleReset} $hierarchy="tertiary" $withoutColor disabled={disabled}>
                {intl.formatMessage({
                  id: 'quicksearch_filter_departure_date_reset',
                })}
              </Button>
              <Button onClick={handleConfirm} disabled={disabled}>
                {disabled && <ButtonLoaderIcon />}
                {intl.formatMessage({
                  id: 'quicksearch_save_button_text',
                })}
              </Button>
            </FooterButtonWrapper>
          </Footer>
        </PopoverContent>
      </Item.AsPopover>
      <Item.AsSheet selectionFormattedText={formattedText?.long} disabled={disabled} data-qa="departure-date-filter">
        <>
          <SheetHeader options={options} />
          <SheetContentWrapper>
            <SheetView options={options} />
          </SheetContentWrapper>
          <SheetFooterStyled $isFixed>
            <SheetContentFooter $view={view}>
              {view === 'calendar' && <Chips options={options} />}
              <SheetButtonWrapper direction="x" spacing={tk('spacing_xs')}>
                <Button onClick={handleReset} $hierarchy="tertiary" $withoutColor disabled={disabled}>
                  {intl.formatMessage({
                    id: 'quicksearch_filter_departure_date_reset',
                  })}
                </Button>
                <Button onClick={handleConfirm} disabled={disabled}>
                  {disabled && <ButtonLoaderIcon />}
                  {intl.formatMessage({
                    id: 'quicksearch_save_button_text',
                  })}
                </Button>
              </SheetButtonWrapper>
            </SheetContentFooter>
          </SheetFooterStyled>
        </>
      </Item.AsSheet>
      <Item.AsDialog selectionFormattedText={formattedText?.long} disabled={disabled} data-qa="departure-date-filter">
        <DialogPrimitive.Root>
          <DialogHeader>
            <DialogPrimitive.HeaderTitle>
              <Typography $variant="body-lg-bold">
                {intl.formatMessage({ id: 'quicksearch_filter_departure_date_title' })}
              </Typography>
              <Button onClick={handleConfirm} $hierarchy="tertiary" $withoutColor>
                <Icon glyph="IconCrossMedium" size="md" />
              </Button>
            </DialogPrimitive.HeaderTitle>
            <DialogPrimitive.HeaderContent>
              <Tabs options={options} />
            </DialogPrimitive.HeaderContent>
          </DialogHeader>
          <DialogPrimitive.Body>
            <DepartureDateFilterByView options={options} />
          </DialogPrimitive.Body>

          <DialogPrimitive.Footer>
            <Footer>
              <Chips options={options} />

              <FooterButtonWrapper direction="x" spacing={tk('spacing_xs')}>
                <Button onClick={handleReset} $hierarchy="tertiary" $withoutColor disabled={disabled}>
                  {intl.formatMessage({
                    id: 'quicksearch_filter_departure_date_reset',
                  })}
                </Button>
                <Button onClick={handleConfirm} disabled={disabled}>
                  {disabled && <ButtonLoaderIcon />}
                  {intl.formatMessage({
                    id: 'quicksearch_save_button_text',
                  })}
                </Button>
              </FooterButtonWrapper>
            </Footer>
          </DialogPrimitive.Footer>

          <DialogFooter>
            <Button onClick={handleReset} $hierarchy="tertiary" $withoutColor disabled={disabled}>
              {intl.formatMessage({
                id: 'quicksearch_filter_departure_date_reset',
              })}
            </Button>
            <Button $fullSize onClick={handleConfirm} disabled={disabled}>
              {disabled && <ButtonLoaderIcon />}
              {intl.formatMessage({
                id: 'quicksearch_save_button_text',
              })}
            </Button>
          </DialogFooter>
        </DialogPrimitive.Root>
      </Item.AsDialog>
      <Item.AsIcon
        selectionFormattedText={formattedText?.long}
        compactSelectionFormattedText={formattedText?.short || formattedText?.long}
      />
    </Item.Root>
  )
}
